import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { getCategories, getCategoryById, getCategoryServices } from '../utils/services';

const SERVICE_AREA = ['Widnes', 'Runcorn', 'Warrington', 'St Helens', 'Liverpool'];

const ServiceCategory = () => {
  const { categoryId } = useParams();
  const categories = getCategories();
  const category = getCategoryById(categoryId);

  if (!category) {
    return (
      <section className="services category-page">
        <div className="services-container">
          <h1 className="page-title">Treatment Category Not Found</h1>
          <p className="page-subtitle">Please return to the treatments page to browse available options.</p>
          <Link to="/services" className="cta-button primary">View All Treatments</Link>
        </div>
      </section>
    );
  }

  const services = getCategoryServices(category);
  const serviceSlugByTitle = new Map(services.map((entry) => [entry.service.title, entry.serviceSlug]));
  const description = `${category.title} in Widnes with a skin-health-first approach by ART Aesthetics & Skincare.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${category.title} - ART Aesthetics & Skincare`,
    serviceType: category.title,
    description,
    url: `https://artaestheticsskincare.com/services/${category.id}`,
    areaServed: SERVICE_AREA,
    provider: {
      '@type': 'MedicalBusiness',
      name: 'ART Aesthetics & Skincare',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Hughes Close',
        addressLocality: 'Widnes',
        postalCode: 'WA8 5EJ',
        addressCountry: 'GB'
      }
    }
  };

  return (
    <section className="services category-page">
      <SEO
        title={`${category.title} in Widnes | ART Aesthetics & Skincare`}
        description={description}
        path={`/services/${category.id}`}
        jsonLd={jsonLd}
      />

      <div className="services-container">
        <Link to="/services" className="category-back-link">← Back to All Treatments</Link>

        <div className="category-page-header">
          <span className="category-icon" aria-hidden="true">{category.icon}</span>
          <h1 className="page-title">{category.title}</h1>
        </div>

        <p className="page-subtitle">
          Serving clients in Widnes and nearby towns with evidence-based skincare and confidence-led treatment planning.
        </p>

        {category.intro && (
          <motion.div
            className="category-intro"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <p>{category.intro}</p>
          </motion.div>
        )}

        {category.services && !category.subcategories && (
          <div className="services-grid">
            {services.map((entry, idx) => (
              <motion.article
                key={entry.serviceSlug}
                className="service-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                viewport={{ once: true }}
              >
                <h3>{entry.service.title}</h3>
                {entry.service.subtitle && <h4 className="service-subtitle">{entry.service.subtitle}</h4>}
                <p className="service-description">{entry.service.description}</p>
                {entry.service.features && entry.service.features.length > 0 && (
                  <>
                    <p className="features-label">Suitable for:</p>
                    <ul className="features-list">
                      {entry.service.features.map((feature) => (
                        <li key={feature}>✨ {feature}</li>
                      ))}
                    </ul>
                  </>
                )}
                {entry.service.note && <p className="service-note">{entry.service.note}</p>}
                <Link to={`/services/${category.id}/${entry.serviceSlug}`} className="service-detail-link">
                  View treatment details →
                </Link>
              </motion.article>
            ))}
          </div>
        )}

        {category.subcategories && category.subcategories.map((subcategory, groupIndex) => (
          <div key={subcategory.title} className="subcategory">
            <h2 className="subcategory-title">{subcategory.title}</h2>
            <div className="services-grid">
              {subcategory.services.map((service, idx) => (
                <motion.article
                  key={service.title}
                  className="service-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: (groupIndex + idx) * 0.06 }}
                  viewport={{ once: true }}
                >
                  <h3>{service.title}</h3>
                  {service.subtitle && <h4 className="service-subtitle">{service.subtitle}</h4>}
                  <p className="service-description">{service.description}</p>
                  {service.features && service.features.length > 0 && (
                    <>
                      <p className="features-label">Suitable for:</p>
                      <ul className="features-list">
                        {service.features.map((feature) => (
                          <li key={feature}>✨ {feature}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {service.note && <p className="service-note">{service.note}</p>}
                  <Link to={`/services/${category.id}/${serviceSlugByTitle.get(service.title)}`} className="service-detail-link">
                    View treatment details →
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        ))}

        <div className="category-internal-links">
          <h2>Continue Your Skin Journey</h2>
          <div className="category-links-grid">
            <Link to="/contact">Ask a Treatment Question</Link>
            <Link to="/find-us">Visit the Clinic in Widnes</Link>
            <Link to="/faq">Read FAQs Before Booking</Link>
          </div>
          <p className="category-mini-links">
            Related categories:{' '}
            {categories
              .filter((entry) => entry.id !== category.id)
              .slice(0, 3)
              .map((entry, index, subset) => (
                <React.Fragment key={entry.id}>
                  <Link to={`/services/${entry.id}`}>{entry.title}</Link>
                  {index < subset.length - 1 ? ' · ' : ''}
                </React.Fragment>
              ))}
          </p>
        </div>

        <motion.div
          className="services-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2>Ready to Book?</h2>
          <p>Use online booking to view current pricing and appointment availability.</p>
          <a
            href="https://that-time.co.uk/art-aesthetics-skincare"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button primary"
          >
            Book Appointment
          </a>
        </motion.div>

        <div className="category-service-count">{services.length} treatment option(s) shown in this category</div>
      </div>
    </section>
  );
};

export default ServiceCategory;